import React, { useEffect, useState, useMemo, useCallback } from "react";
import Tooltip from "../../common/tooltip/tooltip.tsx";
import { ReactComponent as Edit } from "../../images/edit-icon.svg";
import AddAssetDetails from "./addAssetDetails/addAssetDetails.tsx";
import CustomTable from "../../common/table/customTable.tsx";
import {
  addAsset,
  editAsset,
  fetchAssetById,
  loadAsset,
  resetAssetToEdit,
  toggleEnableAsset,
} from "./assetSlice.ts";
import SuccessfullToast from "../../common/toast/successfulToast.tsx";
import { AssetItem } from "./assetService.tsx";
import { formatDateTime } from "../../common/dateConversion/dateUtils.tsx";
import { useAppDispatch, useAppSelector } from "../../hooks/storeHooks.ts";
import useUserAccess from "../../hooks/useUserAcess.ts";
import { useAllyPreviewURL } from "../../hooks/useSettingsURL.ts";
import { Switch } from "@headlessui/react";
import "../../common/toggle/customSwitch.scss";

const AssetDetails: React.FC = () => {
  const dispatch = useAppDispatch();
  const { asset, assetToEdit, assetLoading, assetDeleteLoading } =
    useAppSelector((state) => state.assets);
  const { allyPreviewURL } = useAllyPreviewURL();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [textcolor, setTextColor] = useState("");
  const { isWellAdmin } = useUserAccess();

  useEffect(() => {
    dispatch(loadAsset());
  }, [dispatch]);

  const handleEdit = useCallback(
    async (asset: AssetItem) => {
      try {
        await dispatch(fetchAssetById(asset.id));
        dispatch(resetAssetToEdit());
      } catch (error) {
        console.error("Failed to fetch asset by id", error);
      }
    },
    [dispatch]
  );

  const columns = useMemo(
    () => [
      { Header: "ID", accessor: "id" },
      {
        Header: "Well Name",
        accessor: "name",
        Cell: ({ row }: { row: any }) =>         
          allyPreviewURL ? (
            <a
              href={safeUrl(`${allyPreviewURL}/${row.original.assetGUID}/${row.original.groupName}`)}
              target="_blank"
              rel="noopener noreferrer"
              className="table-row-link"
            >
              {row.original.name}
            </a>
          ) : (
            <span>{row.original.name}</span>
          ),
      },
      { Header: "Customer", accessor: "customerName" },
      { Header: "Node Address", accessor: "nodeAddress" },
      {
        Header: "Created On",
        accessor: "createdOnDate",
        Cell: ({ value }: { value: string }) => formatDateTime(value),
      },
      {
        Header: "Last Modified On",
        accessor: "lastModifiedDate",
        Cell: ({ value }: { value: string }) => formatDateTime(value),
      },
      ...(isWellAdmin
        ? [
            {
              Header: "Well Communications",
              accessor: "isEnabled",
              sortType: "booleanSort",
              Cell: ({ row }: { row: any }) => {
                return (
                  <div style={{ textAlign: "center" }}>
                    <Tooltip
                      content={
                        !row.original.portId || !row.original.nodeAddress
                          ? "Controller Port and Node Address are required to enable the well."
                          : row.original.isEnabled
                          ? "Disable asset"
                          : "Enable asset"
                      }
                      direction="left"
                    >
                      {assetDeleteLoading[row.original.id] ? (
                        <div className="asset-spinner-container">
                          <div className="asset-spinner"></div>
                        </div>
                      ) : (
                        <Switch
                          checked={row.original.isEnabled}
                          onChange={() => {
                            if (
                              !row.original.portId ||
                              !row.original.nodeAddress
                            ) {
                              return;
                            }
                            handleToggleEnable(
                              row.original.id,
                              row.original.isEnabled
                            );
                          }}
                          className={`custom-switch ${
                            row.original.isEnabled ? "checked" : "unchecked"
                          }`}
                          disabled={
                            !row.original.portId || !row.original.nodeAddress
                          }
                        >
                          <span
                            aria-hidden="true"
                            className={`custom-switch-handle ${
                              row.original.isEnabled ? "checked" : ""
                            } ${
                              !row.original.portId || !row.original.nodeAddress
                                ? "disabled"
                                : ""
                            }`}
                          />
                        </Switch>
                      )}
                    </Tooltip>
                  </div>
                );
              },
            },
            {
              Header: "Actions",
              Cell: ({ row }: { row: any }) => {
                return (
                  <div style={{ textAlign: "center", cursor: "pointer" }}>
                    <Tooltip content="Edit asset" direction="top">
                      <div onClick={() => handleEdit(row.original)}>
                        <Edit style={{ stroke: "#fff" }} />
                      </div>
                    </Tooltip>
                  </div>
                );
              },
              disableSortBy: true,
            },
          ]
        : []),
    ],
    [handleEdit, isWellAdmin, allyPreviewURL, assetDeleteLoading]
  );

  const handleToggleEnable = async (assetId: string, isEnabled: boolean) => {
    try {
      const action = toggleEnableAsset({ assetId, isEnabled });
      await dispatch(action).unwrap();
      setToastMessage(
        `Asset has been ${!isEnabled ? "enabled" : "disabled"} successfully`
      );
      setTextColor("green");
      dispatch(loadAsset());
    } catch (errorResp: any) {

          let errorMsg = "";
          if (errorResp.response?.status === 401) {
            errorMsg = "Unauthorized: Customer ID does not exist.";
          } else if (errorResp.response?.status === 404) {
            errorMsg = "Not found: Module or ModuleId is missing for onboarding.";
          } else if (errorResp.response?.status === 500) {
            errorMsg = "Internal server error.";
          } else if (errorResp.response?.status === 400 && errorResp.response?.data?.includes("no valid license")) {
            errorMsg = "License expired: There is no valid license.";
          } else if (errorResp.response?.status === 403 && errorResp.response?.data?.includes("Exceeded")) {
            errorMsg = "Exceeded the allowed well count.";
          }

      setToastMessage(errorMsg || "An unexpected error occurred.");
      setTextColor("red");
    } finally {
      setShowToast(true);
    }
  };

  const addAssetHandler = (AssetItem: AssetItem) => {
    dispatch(addAsset(AssetItem));
  };

  const editAssetHandler = (updatedAsset: AssetItem) => {
    dispatch(editAsset({ assetId: updatedAsset.id, updatedAsset }));
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPageIndex(0);
  };

  const safeUrl = (url: any) => {
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.protocol === "https:" ? parsed.toString() : "/";
    } catch {
      return "/";
    }
  };

  return (
    <div className="page-layout">
      <div className="main-header">
        <div className="header-name">Asset List</div>
      </div>
      <div className="main-content">
        {showToast && (
          <SuccessfullToast
            message={toastMessage}
            isShowEnabled={showToast}
            setShow={setShowToast}
            colorText={textcolor}
          />
        )}
        <CustomTable
          headerComponent={
            <AddAssetDetails
              addAsset={addAssetHandler}
              editAsset={editAssetHandler}
              assetToEdit={assetToEdit}
            />
          }
          data={asset}
          columns={columns}
          handleEdit={handleEdit}
          searchQuery={searchQuery}
          onSearch={handleSearch}
          currentPageIndex={currentPageIndex}
          setCurrentPageIndex={setCurrentPageIndex}
          isLoading={assetLoading}
          csvFileName="asset_data.csv"
        />
      </div>
    </div>
  );
};

export default AssetDetails;
